import { Button, Icon, Tag, Text } from 'components/ui';
import {
  ListingRead,
  PublicStatusEnum,
  VisibilityEnum,
} from 'lib/api/generated';
import {
  getListingStatusLabel,
  getTagStatus,
  getTagStatusBackgroundColor,
  getTagStatusColor,
} from 'lib/helpers';
import { useRouter } from 'next/router';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { useMedia } from 'react-use';
import { PATHS } from 'settings/config';
import styled from 'styled-components';
import { globalTheme } from 'styles/global-theme';
import { breakpoints } from '../../styles/breakpoints';

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: ${globalTheme.space[2]} 80px;
  background: ${globalTheme.colors.highlightBar};
  justify-content: space-between;
  @media screen and (max-width: ${breakpoints.large}) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    > div {
      padding: ${globalTheme.space[0]} 0;
    }
  }
  @media screen and (max-width: ${breakpoints.medium}) {
    flex-direction: column;
    p {
      text-align: center;
    }
  }

  @media screen and (max-width: ${breakpoints.small}) {
    flex-direction: column;
    padding: ${globalTheme.space[2]};

    p {
      text-align: left;
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${globalTheme.space[0]};
`;

const StatusTag = styled(Tag)`
  padding: ${globalTheme.space[1]} 14px;
  min-width: fit-content;
  align-items: center;
  text-transform: capitalize;
`;

const ActionButton = styled(Button)<{ $soldBtn?: boolean }>`
  background-color: ${(props) =>
    props.$soldBtn ? globalTheme.colors.darkRed : globalTheme.colors.blue};
`;

const LeftContent = styled.div`
  display: flex;
  gap: ${globalTheme.space[1]};
  flex: 1;
  align-items: center;
  p {
    width: max-content;
  }
  @media screen and (max-width: ${breakpoints.medium}) {
    align-self: center;
  }

  @media screen and (max-width: ${breakpoints.small}) {
    align-self: flex-start;
  }
`;

interface ListingsDetailProps {
  listingDetails: ListingRead;
  handleShareListing: Function;
  handleMarkAsUnderOfferOrSold?: Function;
}

function ListingDetailHeader({
  listingDetails,
  handleShareListing,
  handleMarkAsUnderOfferOrSold,
}: ListingsDetailProps) {
  const router = useRouter();
  const { id } = router.query;

  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`, false);

  const { public_status, visibility } = listingDetails;
  const isAuction = listingDetails.price_method === 'AUCTION';

  const handleEdit = () => {
    router.push(
      `${PATHS.marketplace.editListing}/${id}?type=${listingDetails.type}`
    );
  };

  const handleSale = () => {
    handleMarkAsUnderOfferOrSold?.();
  };

  const openShareListing = () => {
    handleShareListing();
  };

  if (!isMobile) {
    return (
      <HeaderWrapper>
        <LeftContent>
          <Text fontSize={2} fontWeight='bold'>
            You own this listing.
          </Text>

          {visibility !== VisibilityEnum.Archived &&
            visibility !== VisibilityEnum.Expired &&
            public_status !== PublicStatusEnum.Draft && (
              <StatusTag
                color={getTagStatusColor(
                  public_status as PublicStatusEnum,
                  visibility
                )}
                backgroundColor={getTagStatusBackgroundColor(
                  public_status as PublicStatusEnum,
                  visibility
                )}
              >
                {
                  getTagStatus(public_status as PublicStatusEnum, visibility)
                    .text
                }
              </StatusTag>
            )}

          {public_status === PublicStatusEnum.Draft &&
            visibility !== VisibilityEnum.Hidden && (
              <StatusTag color='white' backgroundColor='red'>
                <Icon component='LockIcon' />
                Private
              </StatusTag>
            )}
          {visibility === VisibilityEnum.Hidden && (
            <StatusTag color='blue' backgroundColor='lightBlue'>
              Draft
            </StatusTag>
          )}

          {visibility === VisibilityEnum.Archived && (
            <StatusTag color='white' backgroundColor='pink'>
              Archived
            </StatusTag>
          )}

          {visibility === VisibilityEnum.Archived && (
            <Text>(Archived Reason: {listingDetails.archived_reason})</Text>
          )}

          {visibility === VisibilityEnum.Expired && (
            <StatusTag color='darkGrey' backgroundColor='neutral'>
              Expired
            </StatusTag>
          )}
          {listingDetails?.sold && (
            <StatusTag color='white' backgroundColor='pink'>
              {isAuction
                ? 'Closed'
                : getListingStatusLabel(listingDetails.type)}
            </StatusTag>
          )}
          {listingDetails?.under_offer && (
            <StatusTag color='pink' backgroundColor='lightPink'>
              Under Offer
            </StatusTag>
          )}
        </LeftContent>
        <ButtonWrapper>
          {visibility !== VisibilityEnum.Hidden &&
            visibility !== VisibilityEnum.Archived &&
            visibility !== VisibilityEnum.Expired &&
            isMobile && (
              <Button size='small' onClick={openShareListing}>
                Share <Icon component='PaperAirplaneIcon' size={1} />
              </Button>
            )}
          <Button size='small' onClick={handleEdit}>
            Edit <Icon component='EditIcon' size={1} />
          </Button>
          {/* An auction's sold state is time-driven (it closes at 6pm on the auction date), so it
              can't be set by hand — the API rejects it. */}
          {!isAuction && (
            <ActionButton $soldBtn size='small' onClick={handleSale}>
              Mark Under Offer or {getListingStatusLabel(listingDetails.type)}
            </ActionButton>
          )}
        </ButtonWrapper>
      </HeaderWrapper>
    );
  } else {
    return <></>;
  }
}

export default ListingDetailHeader;
